/**
 * OverviewTab Component
 *
 * Combines overview components and unified chart for the EventDetail page.
 * Displays KPI metrics, lifecycle status, betting flow, and unified analytics.
 */

import KPIMetrics from '../components/KPIMetrics';
import LifecycleStatus from '../components/LifecycleStatus';
import BettingFlowSection from '../../../components/BettingFlowSection';
import UnifiedEventChart from '../../../components/charts/UnifiedEventChart';
import type { EventDetail } from '../../../types';
import type { EventLifecycleResponse, BettingVolumeResponse } from '../../../api/client';
import type { UseUnifiedChartDataResult } from '../../../hooks/useUnifiedChartData';

interface OverviewTabProps {
  event: EventDetail['event'];
  polymarket: EventDetail['polymarket'] | null;
  polymarketPrice: number | null;
  polymarketChange24h: number | null;
  lastOddsUpdate: string | null;
  priceGap: number | null;
  lifecycleData: EventLifecycleResponse | undefined;
  bettingVolumeData: BettingVolumeResponse | null | undefined;
  unifiedChartResult: UseUnifiedChartDataResult;
}

export default function OverviewTab({
  event,
  polymarket,
  polymarketPrice,
  polymarketChange24h,
  lastOddsUpdate,
  priceGap,
  lifecycleData,
  bettingVolumeData,
  unifiedChartResult,
}: OverviewTabProps) {
  // Get bet type as string or null
  const betType: string | null = polymarket?.betType ?? null;

  return (
    <div className="space-y-6">
      {/* KPI Metrics - 3-column grid */}
      <KPIMetrics
        polymarketPrice={polymarketPrice}
        polymarketChange24h={polymarketChange24h}
        lastOddsUpdate={lastOddsUpdate}
        priceGap={priceGap}
        isLive={event.isLive}
        betType={betType}
      />

      {/* Lifecycle Status Panel */}
      <LifecycleStatus
        lifecycleData={lifecycleData}
        commenceTime={event.commenceTime}
        isLive={event.isLive}
      />

      {/* Betting Flow Section (if data exists) */}
      {bettingVolumeData && (
        <BettingFlowSection
          data={bettingVolumeData}
          homeTeam={event.homeTeam}
          awayTeam={event.awayTeam}
        />
      )}

      {/* Unified Analytics Chart */}
      <div className="card">
        <div className="mb-4">
          <h2 className="text-xl font-bold">UNIFIED ANALYTICS</h2>
          <p className="text-sm text-gray-400 mt-1">
            Combined view of sportsbook odds, Polymarket prices, price gaps, and RLM signals
          </p>
        </div>

        {unifiedChartResult.hasData ? (
          <UnifiedEventChart
            data={unifiedChartResult.chartData}
            homeTeam={event.homeTeam}
            awayTeam={event.awayTeam}
            showOdds={unifiedChartResult.hasOddsData}
            showPolymarket={unifiedChartResult.hasPolyData}
            showGap={unifiedChartResult.hasOddsData && unifiedChartResult.hasPolyData}
            showSignals={unifiedChartResult.hasSignals}
            height={400}
            enableBrush={true}
          />
        ) : (
          <div className="flex items-center justify-center h-64 bg-surface-50 rounded-lg border border-gray-700">
            <div className="text-center text-gray-400">
              <p className="text-lg">No chart data available</p>
              <p className="text-sm mt-1">Data will appear as odds and prices are collected</p>
            </div>
          </div>
        )}

        {/* Chart Legend/Info */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-0.5 bg-blue-500"></span>
            <span className="text-gray-400">Sportsbook Odds (Home)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-0.5 bg-red-500"></span>
            <span className="text-gray-400">Sportsbook Odds (Away)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-0.5 bg-emerald-500" style={{ borderStyle: 'dashed' }}></span>
            <span className="text-gray-400">Polymarket (Home)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-0.5 bg-amber-500" style={{ borderStyle: 'dashed' }}></span>
            <span className="text-gray-400">Polymarket (Away)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
